import { AfterViewInit, Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AgentConversationsInboxComponent } from './components/agent-conversations-inbox/agent-conversations-inbox.component';
import { AgentConversationDetailComponent } from './components/agent-conversation-detail/agent-conversation-detail.component';

/** Margen inferior para que la bandeja no quede pegada al borde de la ventana. */
const MARGEN_INFERIOR_PX = 16;

@Component({
  selector: 'app-agent-conversations-layout',
  standalone: true,
  imports: [CommonModule, RouterModule, AgentConversationsInboxComponent, AgentConversationDetailComponent],
  template: `
    <div #shell class="flex overflow-hidden bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800"
         [style.height.px]="altoDisponible">
      <!-- Inbox -->
      <div class="w-[360px] min-w-[280px] shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col min-h-0">
        <app-agent-conversations-inbox
          [selectedId]="selectedConversationId"
          (conversationSelected)="onConversationSelected($event)">
        </app-agent-conversations-inbox>
      </div>

      <!-- Detalle -->
      <div class="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden">
        <app-agent-conversation-detail
          [conversationId]="selectedConversationId">
        </app-agent-conversation-detail>
      </div>
    </div>
  `
})
export class AgentConversationsLayoutComponent implements OnInit, AfterViewInit {
  selectedConversationId: number | null = null;

  /**
   * Alto en píxeles que la bandeja puede ocupar sin desbordar la ventana (inc66).
   *
   * <p>Se MIDE en vez de calcularse con un `calc(100vh - 64px)` a ojo. Ese valor fijo no
   * contaba ni con el alto real de la cabecera —que cambia entre breakpoints y crece a dos
   * filas por debajo de xl— ni con el padding del contenedor de rutas (`p-4 md:p-6`), así que
   * la página siempre desbordaba unos 60 px: la lista de chats se salía por abajo y el agente
   * tenía que reducir el zoom para llegar a las conversaciones de la parte inferior.</p>
   *
   * <p>Con el alto exacto, cada panel (lista y conversación) se queda con su propio
   * `overflow-y-auto` y la página deja de tener scroll.</p>
   */
  altoDisponible = 600;

  @ViewChild('shell') private shell?: ElementRef<HTMLElement>;

  private readonly destroyRef = inject(DestroyRef);

  constructor(private route: ActivatedRoute, private router: Router) {}

  /**
   * Conversación abierta por enlace directo: `/agent/conversations?id=159`.
   *
   * <p>Se lee de `?id=`, que es EXACTAMENTE lo que escribe {@link onConversationSelected}.
   * Antes se leía el parámetro de ruta `conversationId`, que no existe —la ruta registrada es
   * `agent/conversations`, sin segmento de id—, así que siempre valía null: la URL se
   * actualizaba al elegir un chat, pero pegar ese enlace o recargar la página no
   * preseleccionaba nada. Es también el enlace "Ver la conversación" de la pestaña CSAT.</p>
   */
  ngOnInit() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const id = Number(params.get('id'));
        // Solo se pisa la selección si el enlace trae una conversación válida: navegar sin
        // `id` (o con basura) no debe cerrar la que el agente ya tenía abierta.
        if (Number.isFinite(id) && id > 0 && id !== this.selectedConversationId) {
          this.selectedConversationId = id;
        }
      });
  }

  ngAfterViewInit() {
    this.medirAlto();
  }

  @HostListener('window:resize')
  onResize() {
    this.medirAlto();
  }

  private medirAlto() {
    const el = this.shell?.nativeElement;
    if (!el) return;
    // Distancia desde el inicio del documento, no desde el viewport: si la página estuviera
    // desplazada, `rect.top` daría un valor menor y la bandeja se calcularía más alta de lo
    // que cabe, que es justo el desbordamiento que se quiere evitar.
    const top = el.getBoundingClientRect().top + window.scrollY;
    // setTimeout: la medida se toma después del render actual, así que escribirla de vuelta
    // en el mismo ciclo dispararía ExpressionChangedAfterItHasBeenChecked.
    setTimeout(() => {
      this.altoDisponible = Math.max(320, Math.round(window.innerHeight - top - MARGEN_INFERIOR_PX));
    });
  }

  onConversationSelected(id: number) {
    this.selectedConversationId = id;
    // Update URL without full navigation to avoid auth interceptor issues
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
